import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, Loader2, MapPin, UtensilsCrossed, Music } from 'lucide-react';
import { fetchEvenementPublic } from '../services/api';
import { usePersonnalisation } from '../hooks/usePersonnalisation';
import { nomEdition, periodeEvenement, surPlusieursJours, creneauPrestation } from '../utils/evenement';
import ReservationRepas from '../components/ReservationRepas';
import ReservationConcert from '../components/ReservationConcert';

/**
 * Page dédiée à un événement : le visuel s'y affiche en entier, alors que la
 * liste le recadre en vignette.
 */
export default function EvenementDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const perso = usePersonnalisation();

  // Tant que la personnalisation n'est pas lue, on laisse les blocs se
  // présenter : ils savent déjà s'effacer d'eux-mêmes si rien n'est ouvert.
  const reservationsOuvertes = perso?.options?.repasConcert !== false;

  const { data: evt, isPending, isError } = useQuery({
    queryKey: ['evenement-public', id],
    queryFn: () => fetchEvenementPublic(id as string),
    enabled: Boolean(id),
    retry: false,
  });

  // L'adresse canonique porte le nom de la festivité : un ancien lien
  // numérique est réécrit sans rechargement — « /evenements/1 » devient
  // « /evenements/fete-votive-2026 » dans la barre d'adresse.
  useEffect(() => {
    if (evt?.slug && id !== evt.slug) {
      navigate(`/evenements/${evt.slug}`, { replace: true });
    }
  }, [evt?.slug, id, navigate]);

  if (isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (isError || !evt) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0 space-y-4">
        <p className="text-gray-600 dark:text-gray-300">
          Cet événement n'est pas disponible. Il a peut-être été retiré de l'affiche.
        </p>
        <Link to="/evenements" className="btn-outline inline-flex items-center gap-2 text-sm px-3 py-1.5">
          <ArrowLeft className="w-4 h-4" /> Retour aux événements
        </Link>
      </div>
    );
  }

  // Les créneaux des prestations, quand elles sont ouvertes ET datées : une
  // prestation active sans heure n'a rien à annoncer.
  const creneauRepas = evt.repasActif ? creneauPrestation(evt.repasDate) : null;
  const creneauConcert = evt.concertActif ? creneauPrestation(evt.concertDate) : null;

  return (
    <div className="pb-24 lg:pb-0">
      <div className="p-4 lg:p-0 lg:mb-4">
        <Link
          to="/evenements"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" /> Tous les événements
        </Link>
      </div>

      {evt.imageUrl && (
        // Le visuel est montré en entier : une affiche recadrée perd
        // justement ce qu'elle annonce.
        <div className="bg-gray-900 lg:rounded-2xl overflow-hidden">
          <img
            src={evt.imageUrl}
            alt={evt.nom}
            className="w-full max-h-[75vh] object-contain mx-auto"
          />
        </div>
      )}

      <article className="p-4 lg:p-0 lg:mt-6 space-y-5">
        <h1 className="text-2xl lg:text-4xl font-bold text-gray-900 dark:text-white">{nomEdition(evt.nom, evt.dateDebut)}</h1>

        <div className="flex flex-wrap gap-y-2 gap-x-5 text-sm lg:text-base text-gray-600 dark:text-gray-300">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-primary-600 dark:text-primary-400 shrink-0" />
            <span className="first-letter:uppercase">{periodeEvenement(evt.dateDebut, evt.dateFin)}</span>
          </div>
          {evt.lieu && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-secondary-600 dark:text-secondary-400 shrink-0" />
              {evt.lieu}
            </div>
          )}
          </div>

        {/* Les créneaux du repas et du concert, signalés à part.

            Une fête votive court sur trois jours ; le repas se sert un soir
            précis et le concert se donne un autre. Confondus avec la période
            de la fête, ils envoyaient le convive le mauvais jour — et il
            fallait descendre jusqu'aux blocs de réservation pour lire l'heure
            exacte, ce qu'on ne fait pas quand on croit déjà la connaître.

            Ils ne paraissent que sur une festivité de plusieurs jours : sur
            une soirée unique, la période dit déjà tout, et les répéter
            n'ajouterait que du bruit. */}
        {surPlusieursJours(evt.dateDebut, evt.dateFin) && (creneauRepas || creneauConcert) && (
          <div className="flex flex-wrap gap-2">
            {creneauRepas && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 dark:bg-amber-950/40 px-3 py-1.5 text-sm font-medium text-amber-800 dark:text-amber-200">
                <UtensilsCrossed className="w-4 h-4 shrink-0" aria-hidden="true" />
                Repas <span className="first-letter:uppercase">{creneauRepas}</span>
              </span>
            )}
            {creneauConcert && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 dark:bg-violet-950/40 px-3 py-1.5 text-sm font-medium text-violet-800 dark:text-violet-200">
                <Music className="w-4 h-4 shrink-0" aria-hidden="true" />
                Concert <span className="first-letter:uppercase">{creneauConcert}</span>
              </span>
            )}
          </div>
        )}

        {evt.description && (
          // Le texte est saisi au kilomètre : les retours à la ligne sont
          // préservés, faute de quoi tout se retrouve en un seul bloc.
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line lg:text-lg">
            {evt.description}
          </p>
        )}
      </article>

      {/* Chaque bloc ne s'affiche que si la prestation est ouverte sur cet
          événement. Les deux sont indépendants : on peut servir à manger le
          samedi et donner le concert le dimanche. L'option de la plateforme
          prime sur tout : non souscrite, aucune réservation ne se présente. */}
      {reservationsOuvertes && (
        <>
          <ReservationRepas evenementId={evt.id} />
          <ReservationConcert evenementId={evt.id} />
        </>
      )}
    </div>
  );
}
